import React, { useState, useEffect, useRef } from 'react';
import { Plus, Users, CalendarCheck, FileClock, CalendarRange, Wallet, Printer, Camera, ImageIcon } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import DataGrid from '../../components/DataGrid';
import SearchSelect from '../../components/SearchSelect';
import {
  staff as staffApi,
  attendance as attendanceApi,
  leaveRequests as leaveApi,
  shifts as shiftApi,
  payroll as payrollApi,
  uploads as uploadsApi,
  type Staff,
  type StaffAttendance,
  type LeaveRequest,
  type ShiftSchedule,
  type Payslip,
  type ApiError,
} from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

type Tab = 'registry' | 'attendance' | 'leave' | 'shifts' | 'payroll';

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const STAFF_CATEGORIES: Staff['category'][] = ['SECURITY', 'HOUSEKEEPING', 'GARDENING', 'OTHER'];
const EMPLOYMENT_TYPES: Staff['employmentType'][] = ['FULL_TIME', 'PART_TIME', 'CONTRACT'];

const StaffPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const [tab, setTab] = useState<Tab>('registry');
  const [staffList, setStaffList] = useState<Staff[]>([]);

  useEffect(() => {
    if (!societyId) return;
    staffApi.list(societyId).then(setStaffList).catch(() => {});
  }, [societyId, tab]);

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Staff & Payroll
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Registry, attendance, leave, shifts and payroll for society staff.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {([
          ['registry', 'Registry'], ['attendance', 'Attendance'], ['leave', 'Leave'],
          ['shifts', 'Shifts'], ['payroll', 'Payroll'],
        ] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t} onClick={() => setTab(t)}
            className={tab === t ? 'nx-btn nx-btn-primary nx-btn-sm' : 'nx-btn nx-btn-outline nx-btn-sm'}
          >
            {label}
          </button>
        ))}
      </div>

      {!societyId ? null : tab === 'registry' ? <RegistryTab societyId={societyId} staffList={staffList} onChange={setStaffList} />
        : tab === 'attendance' ? <AttendanceTab societyId={societyId} staffList={staffList} />
        : tab === 'leave' ? <LeaveTab societyId={societyId} staffList={staffList} />
        : tab === 'shifts' ? <ShiftsTab societyId={societyId} staffList={staffList} />
        : <PayrollTab societyId={societyId} staffList={staffList} />}
    </PageLayout>
  );
};

const staffName = (staffList: Staff[], id: string) => staffList.find(s => s.id === id)?.name ?? id.slice(0, 8);

const RegistryTab: React.FC<{ societyId: string; staffList: Staff[]; onChange: (s: Staff[]) => void }> = ({ societyId, staffList, onChange }) => {
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', phone: '', category: 'SECURITY' as Staff['category'], employmentType: 'FULL_TIME' as Staff['employmentType'],
    joiningDate: '', salary: '0',
  });

  useEffect(() => { setLoading(false); }, [staffList]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const created = await staffApi.create(societyId, {
        name: form.name, phone: form.phone || undefined, category: form.category,
        employmentType: form.employmentType, joiningDate: form.joiningDate || undefined,
        salary: Number(form.salary),
      });
      onChange([created, ...staffList]);
      setShowForm(false);
      setForm({ name: '', phone: '', category: 'SECURITY', employmentType: 'FULL_TIME', joiningDate: '', salary: '0' });
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add staff member');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Staff
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'name', header: 'Name', cellStyle: { fontWeight: 500 }, render: (s: Staff) => s.name },
            { key: 'category', header: 'Category', render: (s: Staff) => s.category },
            { key: 'employment', header: 'Employment', render: (s: Staff) => s.employmentType.replace('_', ' ') },
            { key: 'joining', header: 'Joined', render: (s: Staff) => s.joiningDate || '—' },
            { key: 'salary', header: 'Salary', render: (s: Staff) => fmtAmount(s.salary) },
            {
              key: 'ids', header: 'User ID / Society ID',
              cellStyle: { color: 'var(--nx-text-muted)', fontSize: 11, fontFamily: 'monospace' },
              render: (s: Staff) => (
                <span title={`User ID: ${s.userId ?? '—'}\nSociety ID: ${s.societyId}`}>
                  {s.userId ? `${s.userId.slice(0, 8)}…` : '—'} / {s.societyId.slice(0, 8)}…
                </span>
              ),
            },
            {
              key: 'status', header: 'Status',
              render: (s: Staff) => (
                <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: s.active ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)', color: s.active ? '#10B981' : '#64748B' }}>
                  {s.active ? 'Active' : 'Inactive'}
                </span>
              ),
            },
          ]}
          rows={staffList}
          rowKey={s => s.id}
          loading={loading}
          emptyIcon={<Users size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No staff added yet."
        />
      </div>
      {showForm && (
        <Modal title="Add Staff Member" submitLabel="Add Staff" error={error} isLoading={saving} onClose={() => setShowForm(false)} onSubmit={submit}>
          <div>
            <label style={lbl}>Name *</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value as Staff['category'] }))} style={inp}>
                {STAFF_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Employment Type</label>
              <select value={form.employmentType} onChange={e => setForm(p => ({ ...p, employmentType: e.target.value as Staff['employmentType'] }))} style={inp}>
                {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Phone</label>
              <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={lbl}>Joining Date</label>
              <input type="date" value={form.joiningDate} onChange={e => setForm(p => ({ ...p, joiningDate: e.target.value }))} style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Monthly Salary (₹)</label>
            <input type="number" value={form.salary} onChange={e => setForm(p => ({ ...p, salary: e.target.value }))} style={inp} />
          </div>
        </Modal>
      )}
    </>
  );
};

const AttendanceTab: React.FC<{ societyId: string; staffList: Staff[] }> = ({ societyId, staffList }) => {
  const [date, setDate] = useState(todayIso());
  const [records, setRecords] = useState<StaffAttendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState<string | null>(null);
  const [pendingSelfieStaffId, setPendingSelfieStaffId] = useState<string | null>(null);
  const selfieInputRef = useRef<HTMLInputElement>(null);

  const refresh = () => {
    setLoading(true);
    attendanceApi.listByDate(societyId, date).then(setRecords).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(refresh, [societyId, date]);

  const statusFor = (staffId: string) => records.find(r => r.staffId === staffId)?.status;

  const mark = async (staffId: string, status: StaffAttendance['status']) => {
    setMarking(staffId);
    try {
      await attendanceApi.mark(societyId, staffId, { attendanceDate: date, status });
      refresh();
    } catch { /* ignore */ } finally { setMarking(null); }
  };

  const triggerSelfieCapture = (staffId: string) => {
    setPendingSelfieStaffId(staffId);
    selfieInputRef.current?.click();
  };

  const handleSelfieSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const staffId = pendingSelfieStaffId;
    e.target.value = '';
    if (!file || !staffId) return;
    setMarking(staffId);
    try {
      const uploaded = await uploadsApi.upload(societyId, file);
      await attendanceApi.mark(societyId, staffId, { attendanceDate: date, status: 'PRESENT', checkInSelfieUrl: uploaded.url });
      refresh();
    } catch { /* ignore */ } finally { setMarking(null); setPendingSelfieStaffId(null); }
  };

  const statusColor = (s?: string) =>
    s === 'PRESENT' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
      : s === 'ABSENT' ? { bg: 'rgba(239,68,68,0.1)', text: '#DC2626' }
      : s === 'HALF_DAY' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
      : s === 'ON_LEAVE' ? { bg: 'rgba(26,108,246,0.1)', text: '#1A6CF6' }
      : { bg: 'rgba(148,163,184,0.1)', text: '#64748B' };

  return (
    <>
      <div className="nx-card" style={{ padding: 16, marginBottom: 16 }}>
        <label style={lbl}>Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, maxWidth: 220 }} />
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'name', header: 'Name', cellStyle: { fontWeight: 500 }, render: (s: Staff) => s.name },
            { key: 'category', header: 'Category', render: (s: Staff) => s.category },
            {
              key: 'status', header: 'Status',
              render: (s: Staff) => {
                const st = statusFor(s.id);
                const sc = statusColor(st);
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                    {st ? st.replace('_', ' ') : 'Not marked'}
                  </span>
                );
              },
            },
            {
              key: 'actions', header: '',
              render: (s: Staff) => {
                const selfieUrl = records.find(r => r.staffId === s.id)?.checkInSelfieUrl;
                return (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(['PRESENT', 'ABSENT', 'HALF_DAY', 'ON_LEAVE'] as const).map(st => (
                      <button
                        key={st} className="nx-btn nx-btn-outline nx-btn-sm" disabled={marking === s.id}
                        onClick={() => mark(s.id, st)}
                      >
                        {st === 'PRESENT' ? 'P' : st === 'ABSENT' ? 'A' : st === 'HALF_DAY' ? 'H' : 'L'}
                      </button>
                    ))}
                    {selfieUrl ? (
                      <a href={selfieUrl} target="_blank" rel="noopener noreferrer" className="nx-btn nx-btn-outline nx-btn-sm" title="View check-in selfie">
                        <ImageIcon size={12} />
                      </a>
                    ) : (
                      <button
                        className="nx-btn nx-btn-outline nx-btn-sm" disabled={marking === s.id}
                        onClick={() => triggerSelfieCapture(s.id)}
                        title="Capture check-in selfie"
                      >
                        <Camera size={12} />
                      </button>
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={staffList}
          rowKey={s => s.id}
          loading={loading}
          emptyIcon={<CalendarCheck size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No staff to mark attendance for."
        />
        <input
          ref={selfieInputRef} type="file" accept="image/*" capture="environment"
          style={{ display: 'none' }} onChange={handleSelfieSelected}
        />
      </div>
    </>
  );
};

const LeaveTab: React.FC<{ societyId: string; staffList: Staff[] }> = ({ societyId, staffList }) => {
  const [items, setItems] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ staffId: '', leaveType: 'CASUAL', fromDate: '', toDate: '', reason: '' });
  const [deciding, setDeciding] = useState<string | null>(null);

  const refresh = () => {
    leaveApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(refresh, [societyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.staffId) return;
    setSaving(true); setError('');
    try {
      await leaveApi.create(societyId, form);
      setShowForm(false);
      setForm({ staffId: '', leaveType: 'CASUAL', fromDate: '', toDate: '', reason: '' });
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to raise leave request');
    } finally { setSaving(false); }
  };

  const decide = async (id: string, status: 'APPROVED' | 'REJECTED') => {
    setDeciding(id);
    try {
      await leaveApi.decide(societyId, id, status);
      refresh();
    } catch { /* ignore */ } finally { setDeciding(null); }
  };

  const statusColor = (s: string) =>
    s === 'APPROVED' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
      : s === 'REJECTED' ? { bg: 'rgba(239,68,68,0.1)', text: '#DC2626' }
      : { bg: 'rgba(245,158,11,0.1)', text: '#D97706' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Raise Leave Request
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'staff', header: 'Staff', cellStyle: { fontWeight: 500 }, render: (l: LeaveRequest) => staffName(staffList, l.staffId) },
            { key: 'type', header: 'Type', render: (l: LeaveRequest) => l.leaveType },
            { key: 'period', header: 'Period', render: (l: LeaveRequest) => `${l.fromDate} → ${l.toDate}` },
            {
              key: 'status', header: 'Status',
              render: (l: LeaveRequest) => {
                const sc = statusColor(l.status);
                return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>{l.status}</span>;
              },
            },
            {
              key: 'actions', header: '',
              render: (l: LeaveRequest) => l.status === 'PENDING' ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="nx-btn nx-btn-outline nx-btn-sm" disabled={deciding === l.id} onClick={() => decide(l.id, 'APPROVED')}>Approve</button>
                  <button className="nx-btn nx-btn-outline nx-btn-sm" disabled={deciding === l.id} onClick={() => decide(l.id, 'REJECTED')}>Reject</button>
                </div>
              ) : null,
            },
          ]}
          rows={items}
          rowKey={l => l.id}
          loading={loading}
          emptyIcon={<FileClock size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No leave requests yet."
        />
      </div>
      {showForm && (
        <Modal title="Raise Leave Request" submitLabel="Submit" error={error} isLoading={saving} onClose={() => setShowForm(false)} onSubmit={submit}>
          <div>
            <label style={lbl}>Staff *</label>
            <SearchSelect
              value={form.staffId}
              onChange={v => setForm(p => ({ ...p, staffId: v }))}
              options={staffList.map(s => ({ value: s.id, label: s.name }))}
              placeholder="Search staff…"
              required
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Type</label>
              <select value={form.leaveType} onChange={e => setForm(p => ({ ...p, leaveType: e.target.value }))} style={inp}>
                <option value="SICK">Sick</option>
                <option value="CASUAL">Casual</option>
                <option value="EARNED">Earned</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label style={lbl}>From *</label>
              <input type="date" value={form.fromDate} onChange={e => setForm(p => ({ ...p, fromDate: e.target.value }))} required style={inp} />
            </div>
            <div>
              <label style={lbl}>To *</label>
              <input type="date" value={form.toDate} onChange={e => setForm(p => ({ ...p, toDate: e.target.value }))} required style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Reason</label>
            <textarea value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} style={{ ...inp, minHeight: 70, resize: 'vertical' }} />
          </div>
        </Modal>
      )}
    </>
  );
};

const ShiftsTab: React.FC<{ societyId: string; staffList: Staff[] }> = ({ societyId, staffList }) => {
  const [items, setItems] = useState<ShiftSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ staffId: '', shiftDate: todayIso(), shiftType: 'MORNING' as ShiftSchedule['shiftType'], startTime: '', endTime: '', notes: '' });

  const refresh = () => {
    shiftApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(refresh, [societyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.staffId) return;
    if (form.shiftType === 'CUSTOM' && (!form.startTime || !form.endTime)) {
      setError('Start and end time are required for a custom shift');
      return;
    }
    setSaving(true); setError('');
    try {
      await shiftApi.create(societyId, {
        staffId: form.staffId, shiftDate: form.shiftDate, shiftType: form.shiftType,
        startTime: form.shiftType === 'CUSTOM' ? form.startTime : undefined,
        endTime: form.shiftType === 'CUSTOM' ? form.endTime : undefined,
        notes: form.notes,
      });
      setShowForm(false);
      setForm({ staffId: '', shiftDate: todayIso(), shiftType: 'MORNING', startTime: '', endTime: '', notes: '' });
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to assign shift');
    } finally { setSaving(false); }
  };

  const shiftLabel = (s: ShiftSchedule) =>
    s.shiftType === 'CUSTOM' && s.startTime && s.endTime
      ? `Custom (${s.startTime.slice(0, 5)}–${s.endTime.slice(0, 5)})`
      : s.shiftType.charAt(0) + s.shiftType.slice(1).toLowerCase();

  const remove = async (id: string) => {
    await shiftApi.delete(societyId, id).catch(() => {});
    refresh();
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Assign Shift
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'staff', header: 'Staff', cellStyle: { fontWeight: 500 }, render: (s: ShiftSchedule) => staffName(staffList, s.staffId) },
            { key: 'date', header: 'Date', render: (s: ShiftSchedule) => s.shiftDate },
            { key: 'type', header: 'Shift', render: (s: ShiftSchedule) => shiftLabel(s) },
            { key: 'notes', header: 'Notes', render: (s: ShiftSchedule) => s.notes || '—' },
            {
              key: 'actions', header: '',
              render: (s: ShiftSchedule) => (
                <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => remove(s.id)}>Remove</button>
              ),
            },
          ]}
          rows={items}
          rowKey={s => s.id}
          loading={loading}
          emptyIcon={<CalendarRange size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No shifts assigned yet."
        />
      </div>
      {showForm && (
        <Modal title="Assign Shift" submitLabel="Assign" error={error} isLoading={saving} onClose={() => setShowForm(false)} onSubmit={submit}>
          <div>
            <label style={lbl}>Staff *</label>
            <SearchSelect
              value={form.staffId}
              onChange={v => setForm(p => ({ ...p, staffId: v }))}
              options={staffList.map(s => ({ value: s.id, label: s.name }))}
              placeholder="Search staff…"
              required
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Date *</label>
              <input type="date" value={form.shiftDate} onChange={e => setForm(p => ({ ...p, shiftDate: e.target.value }))} required style={inp} />
            </div>
            <div>
              <label style={lbl}>Shift</label>
              <select value={form.shiftType} onChange={e => setForm(p => ({ ...p, shiftType: e.target.value as ShiftSchedule['shiftType'] }))} style={inp}>
                <option value="MORNING">Morning</option>
                <option value="EVENING">Evening</option>
                <option value="NIGHT">Night</option>
                <option value="CUSTOM">Custom (set times)</option>
              </select>
            </div>
          </div>
          {form.shiftType === 'CUSTOM' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lbl}>Start Time *</label>
                <input type="time" value={form.startTime} onChange={e => setForm(p => ({ ...p, startTime: e.target.value }))} required style={inp} />
              </div>
              <div>
                <label style={lbl}>End Time *</label>
                <input type="time" value={form.endTime} onChange={e => setForm(p => ({ ...p, endTime: e.target.value }))} required style={inp} />
              </div>
            </div>
          )}
          <div>
            <label style={lbl}>Notes</label>
            <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={inp} />
          </div>
        </Modal>
      )}
    </>
  );
};

const PayrollTab: React.FC<{ societyId: string; staffList: Staff[] }> = ({ societyId, staffList }) => {
  const [items, setItems] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ staffId: '', month: String(new Date().getMonth() + 1), year: String(new Date().getFullYear()), deductions: '0', bonus: '0' });
  const [printSlip, setPrintSlip] = useState<Payslip | null>(null);
  const [paying, setPaying] = useState<string | null>(null);

  const refresh = () => {
    payrollApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(refresh, [societyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.staffId) return;
    setSaving(true); setError('');
    try {
      await payrollApi.generate(societyId, form.staffId, {
        month: Number(form.month), year: Number(form.year),
        deductions: Number(form.deductions), bonus: Number(form.bonus),
      });
      setShowForm(false);
      setForm({ staffId: '', month: String(new Date().getMonth() + 1), year: String(new Date().getFullYear()), deductions: '0', bonus: '0' });
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to generate payslip');
    } finally { setSaving(false); }
  };

  const markPaid = async (id: string) => {
    setPaying(id);
    try {
      await payrollApi.markPaid(societyId, id);
      refresh();
    } catch { /* ignore */ } finally { setPaying(null); }
  };

  const statusColor = (s: string) =>
    s === 'PAID' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' } : { bg: 'rgba(245,158,11,0.1)', text: '#D97706' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Generate Payslip
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'staff', header: 'Staff', cellStyle: { fontWeight: 500 }, render: (p: Payslip) => staffName(staffList, p.staffId) },
            { key: 'period', header: 'Period', render: (p: Payslip) => `${p.month}/${p.year}` },
            { key: 'net', header: 'Net Pay', render: (p: Payslip) => fmtAmount(p.netPay) },
            {
              key: 'status', header: 'Status',
              render: (p: Payslip) => {
                const sc = statusColor(p.status);
                return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>{p.status}</span>;
              },
            },
            {
              key: 'actions', header: '',
              render: (p: Payslip) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setPrintSlip(p)} style={{ gap: 4 }}>
                    <Printer size={12} /> View
                  </button>
                  {p.status !== 'PAID' && (
                    <button className="nx-btn nx-btn-outline nx-btn-sm" disabled={paying === p.id} onClick={() => markPaid(p.id)}>
                      Mark Paid
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={p => p.id}
          loading={loading}
          emptyIcon={<Wallet size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No payslips generated yet."
        />
      </div>
      {showForm && (
        <Modal title="Generate Payslip" submitLabel="Generate" error={error} isLoading={saving} onClose={() => setShowForm(false)} onSubmit={submit}>
          <div>
            <label style={lbl}>Staff *</label>
            <SearchSelect
              value={form.staffId}
              onChange={v => setForm(p => ({ ...p, staffId: v }))}
              options={staffList.map(s => ({ value: s.id, label: `${s.name} (${fmtAmount(s.salary)})` }))}
              placeholder="Search staff…"
              required
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Month *</label>
              <input type="number" min="1" max="12" value={form.month} onChange={e => setForm(p => ({ ...p, month: e.target.value }))} required style={inp} />
            </div>
            <div>
              <label style={lbl}>Year *</label>
              <input type="number" value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} required style={inp} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Deductions (₹)</label>
              <input type="number" value={form.deductions} onChange={e => setForm(p => ({ ...p, deductions: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={lbl}>Bonus (₹)</label>
              <input type="number" value={form.bonus} onChange={e => setForm(p => ({ ...p, bonus: e.target.value }))} style={inp} />
            </div>
          </div>
        </Modal>
      )}

      {printSlip && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} className="nx-print-overlay">
          <div onClick={() => setPrintSlip(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(8,14,29,0.75)' }} />
          <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420, background: '#fff', color: '#0f172a', borderRadius: 12, padding: 28 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Payslip</h2>
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>{staffName(staffList, printSlip.staffId)} — {printSlip.month}/{printSlip.year}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Basic Pay</span><span>{fmtAmount(printSlip.basicPay)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Bonus</span><span>+{fmtAmount(printSlip.bonus)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Deductions</span><span>-{fmtAmount(printSlip.deductions)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                <span>Net Pay</span><span>{fmtAmount(printSlip.netPay)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setPrintSlip(null)}>Close</button>
              <button className="nx-btn nx-btn-primary nx-btn-sm" onClick={() => window.print()}>Print</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StaffPage;
